import Vue, { onBeforeUnmount, ref, type Component, type Ref } from 'vue';
import VueRouter, { type RawLocation, type Route, type RouteConfig } from 'vue-router';
import FlipRouterView from '../ui/FlipRouterView.vue';

Vue.use(VueRouter);

export type FlipDirection = 'forward' | 'back';

export interface UseFlipRouter {
  RouterView: Component;
  router: VueRouter;
  route: Ref<Route>;
  direction: Ref<FlipDirection>;
  push: (location: RawLocation) => void;
  replace: (location: RawLocation) => void;
  back: () => void;
}

export interface FlipRoute {
  path: string;
  name?: string;
  startDirection?: FlipDirection;
  endDirection?: FlipDirection;
  component: unknown;
  props?: RouteConfig['props'];
}

export interface UseFlipRouterOptions {
  routes: FlipRoute[];
  initial?: RawLocation;
}

function noop(): void {
  return undefined;
}

export function useFlipRouter(options: UseFlipRouterOptions): UseFlipRouter {
  const router = new VueRouter({ mode: 'abstract', routes: options.routes as RouteConfig[] });
  const direction = ref<FlipDirection>('forward');
  const route = ref<Route>(router.currentRoute) as Ref<Route>;

  const readIndex = (): number => {
    const history = (router as unknown as { history?: { index?: number } }).history;
    return typeof history?.index === 'number' ? history.index : 0;
  };
  let prevIndex = readIndex();

  const stopAfterEach = router.afterEach((to: Route, from: Route) => {
    const toFoundRoute = options.routes.find((r) => r.name === to.name);
    const fromFoundRoute = options.routes.find((r) => r.name === from.name);
    const nextIndex = readIndex();
    if (nextIndex > prevIndex) {
      direction.value = toFoundRoute?.startDirection || 'forward';
    } else if (nextIndex < prevIndex) {
      direction.value = fromFoundRoute?.endDirection || 'back';
    }
    prevIndex = nextIndex;
    route.value = to;
  });

  const start = options.initial ?? options.routes[0]?.path ?? '/';
  router.push(start, noop, noop);
  prevIndex = readIndex();
  route.value = router.currentRoute;

  const push = (location: RawLocation): void => {
    router.push(location, noop, noop);
  };

  const replace = (location: RawLocation): void => {
    router.replace(location, noop, noop);
  };

  const back = (): void => {
    router.back();
  };

  onBeforeUnmount(() => {
    stopAfterEach();
  });

  const RouterView: Component = Vue.extend({
    name: 'FlipRouterHost',
    router,
    render(createElement) {
      return createElement(FlipRouterView, {
        props: { direction: direction.value },
        scopedSlots: this.$scopedSlots,
      });
    },
  });

  return { RouterView, router, route, direction, push, replace, back };
}
